<div class="page-grid-container fa-width-auto logistics-dashboard-page">
    <section class="logistics-workspace" id="search-area" ngForm #searchForm="ngForm">
        <header class="logistics-topbar">
            <div class="logistics-brand">
                <span class="logistics-brand__mark"><i class="fal fa-truck-fast"></i></span>
                <span><strong>ZINT</strong><small>Logistics intelligence</small></span>
            </div>
            <div class="logistics-topbar__actions">
                <span class="logistics-coverage-pill">
                    <i class="fas fa-circle"></i>
                    {{ logisticsLoadedMagicFilterCount }}/{{ logisticsExpectedMagicFilterCount }} intelligence filters available
                </span>
                @if (!showLogisticsHero) {
                <button type="button" class="logistics-text-link logistics-show-introduction" (click)="revealLogisticsHero()">
                    <i class="fal fa-circle-info"></i> Show introduction
                </button>
                }
                <button type="button" class="logistics-text-link" (click)="switchDashboardMode('full')">
                    Switch to full platform <i class="fal fa-arrow-up-right"></i>
                </button>
            </div>
        </header>

        @if (showLogisticsHero) {
        <div class="logistics-hero">
            <button type="button" class="logistics-hero__dismiss" (click)="dismissLogisticsHero()"
                aria-label="Hide the Logistics introduction">
                <span>Hide introduction</span><i class="fal fa-xmark"></i>
            </button>
            <div class="logistics-hero__copy">
                <span class="logistics-eyebrow">PURPOSE-BUILT PROSPECTING</span>
                <h1>Find businesses that need<br><em>your logistics expertise.</em></h1>
                <p>Combine company fundamentals with parcel, freight and warehousing intelligence to build a target list you can act on.</p>
            </div>
            <div class="logistics-hero__metrics" aria-label="Workspace coverage">
                <div><strong>7</strong><span>qualification sections</span></div>
                <div><strong>{{ logisticsExpectedMagicFilterCount }}</strong><span>intelligence filters</span></div>
                <div><strong>{{ logisticsMagicQuestionCount }}</strong><span>profile insights</span></div>
            </div>
        </div>
        }

        <div class="logistics-query-bar" [class.logistics-query-bar--hero-hidden]="!showLogisticsHero">
            <div>
                <span class="logistics-query-bar__label">YOUR PROSPECT CRITERIA</span>
                <strong>{{ logisticsActiveFilterCount }} active {{ logisticsActiveFilterCount === 1 ? 'filter' : 'filters' }}</strong>
                <span class="logistics-query-bar__hint">Start broad, then add the operational signals that matter.</span>
            </div>
            <div class="logistics-query-bar__actions">
                <button type="button" class="logistics-reset-button" (click)="resetLogisticsFilters()">
                    <i class="fal fa-undo"></i> Reset
                </button>
                <button type="button" class="logistics-primary-button" (click)="searchCompaniesAfterFilters(true)"
                    [disabled]="isLoading().loadingUI || isLoading().loadingCompanies || isReadOnlyUser">
                    <i class="fal fa-search"></i>
                    <span>{{ isLoading().loadingCompanies ? 'Searching…' : 'Find prospects' }}</span>
                </button>
            </div>
        </div>

        @if (showLogisticsStickySearch) {
        <aside class="logistics-sticky-search" aria-label="Prospect search actions">
            <span><strong>{{ logisticsActiveFilterCount }}</strong> active</span>
            <button type="button" class="logistics-primary-button" (click)="searchCompaniesAfterFilters(true)"
                [disabled]="isLoading().loadingUI || isLoading().loadingCompanies || isReadOnlyUser">
                <i class="fal fa-search"></i>
                <span>{{ isLoading().loadingCompanies ? 'Searching…' : 'Find prospects' }}</span>
            </button>
        </aside>
        }

        @if (availableTerritories?.length) {
        <section class="logistics-search-scope" aria-label="Search scope">
            <span class="logistics-search-scope__icon"><i class="fal fa-globe-europe"></i></span>
            <div>
                <strong>Search within</strong>
                <small>Use one of your organisation's prospect territories</small>
            </div>
            <select name="selectedTerritory" [(ngModel)]="selectedTerritory"
                [compareWith]="compareTerritories" aria-label="Selected territory">
                <option *ngIf="!areTerritoriesEnforced" [ngValue]="null">All permitted companies</option>
                <option *ngFor="let territory of availableTerritories" [ngValue]="territory">
                    {{ territory.name }}
                </option>
            </select>
            @if (areTerritoriesEnforced) {
            <span class="logistics-search-scope__locked"><i class="fal fa-lock"></i> Enforced by your organisation</span>
            }
        </section>
        }

        <div class="logistics-section-heading">
            <div>
                <span class="logistics-section-number">01</span>
                <div><span class="logistics-eyebrow">THE FOUNDATIONS</span><h2>Shape your ideal customer</h2></div>
            </div>
        </div>

        <div class="logistics-filter-stack" [class.loading-ui]="isLoading().loadingUI">
            <details class="logistics-filter-card">
                <summary>
                    <span class="logistics-filter-card__icon"><i class="fal fa-bullseye-arrow"></i></span>
                    <span><strong>Ideal customer</strong><small>The products and services that create logistics demand</small></span>
                    <i class="fal fa-chevron-down logistics-filter-card__chevron"></i>
                </summary>
                <div class="logistics-filter-card__body">
                    <div class="logistics-foundation-layout logistics-foundation-layout--ideal">
                        <section class="logistics-foundation-block logistics-foundation-block--concept">
                            <label for="logisticsProductSearch" class="logistics-field-label"><i class="fal fa-box-open"></i> Products or services</label>
                            <textarea id="logisticsProductSearch" name="summarySemanticSearchTerm"
                                [(ngModel)]="summarySemanticSearchTerm" rows="3"
                                placeholder="e.g. online furniture retailers, food manufacturers"
                                (keyup.control.enter)="testIfSearchCompaniesOnEnterPress($event)"></textarea>
                            <div class="logistics-quick-picks" aria-label="Example prospect types">
                                <button type="button" (click)="summarySemanticSearchTerm = 'manufacturers'">Manufacturers</button>
                                <button type="button" (click)="summarySemanticSearchTerm = 'online and e-commerce retailers'">E-commerce</button>
                                <button type="button" (click)="summarySemanticSearchTerm = 'wholesalers and distributors'">Wholesale</button>
                            </div>
                            <span class="logistics-balanced-match"><i class="fal fa-sliders-simple"></i> Balanced match quality</span>
                        </section>

                        <section class="logistics-foundation-block logistics-foundation-block--industries">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-industry-windows"></i></span>
                                <div><strong>Industries</strong><small>Browse or search official SIC classifications</small></div>
                            </header>
                            <div class="logistics-segmented-control" aria-label="SIC selection type">
                                <button type="button" [class.active]="logisticsSicSelectionMode === 'include'"
                                    (click)="logisticsSicSelectionMode = 'include'">Include</button>
                                <button type="button" [class.active]="logisticsSicSelectionMode === 'exclude'"
                                    (click)="logisticsSicSelectionMode = 'exclude'">Exclude</button>
                            </div>
                            <div class="logistics-input-action logistics-input-action--quiet">
                                <input type="search" name="sicCodeTextSearch" [(ngModel)]="sicCodeTextSearch"
                                    (ngModelChange)="searchSicCodes()" placeholder="Search industries or SIC codes" />
                                <button type="button" (click)="resetSicSearch()" aria-label="Reset industry search">
                                    <i class="fal fa-undo"></i>
                                </button>
                            </div>
                            <div class="logistics-tree-list logistics-tree-list--sic">
                                <ng-container *ngFor="let sicCodeChoice of companySICCodeOptions">
                                    <div class="logistics-tree-row"
                                        *ngIf="(sicCodeChoice.type === 'section' && !isSearchingSicCodes) || sicCodeChoice.shouldShow">
                                        <label [style.padding-left.rem]="sicCodeChoice.level * 0.75">
                                            <input type="checkbox"
                                                [checked]="isLogisticsSicSelected(sicCodeChoice)"
                                                (change)="setLogisticsSicSelection(sicCodeChoice, $any($event.target).checked)" />
                                            <span><strong>{{ sicCodeChoice.identifier }}</strong> {{ sicCodeChoice.description }}</span>
                                        </label>
                                        <button *ngIf="sicCodeChoice.children?.length" type="button"
                                            (click)="expandOrCollapseSICCodes(sicCodeChoice.id)"
                                            [attr.aria-label]="'Expand ' + sicCodeChoice.description">
                                            <i class="fal fa-chevron-down"></i>
                                        </button>
                                    </div>
                                </ng-container>
                            </div>
                        </section>

                        <section class="logistics-foundation-block logistics-foundation-block--contact">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-address-card"></i></span>
                                <div><strong>Contactable through</strong><small>Require a public company contact channel</small></div>
                            </header>
                            <div class="logistics-check-grid logistics-check-grid--contact">
                                <label>
                                    <input type="checkbox" name="hasPhoneNumber" [(ngModel)]="hasPhoneNumber" />
                                    <span><i class="fal fa-phone"></i> Phone available</span>
                                </label>
                                <label>
                                    <input type="checkbox" name="hasEmailAddress" [(ngModel)]="hasEmailAddress" />
                                    <span><i class="fal fa-envelope"></i> Email available</span>
                                </label>
                            </div>
                        </section>
                    </div>
                </div>
            </details>

            <details class="logistics-filter-card">
                <summary>
                    <span class="logistics-filter-card__icon"><i class="fal fa-map-marker-alt"></i></span>
                    <span><strong>Geography</strong><small>Where they operate</small></span>
                    <i class="fal fa-chevron-down logistics-filter-card__chevron"></i>
                </summary>
                <div class="logistics-filter-card__body">
                    <div class="logistics-foundation-layout logistics-foundation-layout--geography">
                        <section class="logistics-foundation-block logistics-foundation-block--coverage">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-map"></i></span>
                                <div><strong>Address coverage</strong><small>Choose which company locations should qualify</small></div>
                            </header>
                            <label for="logisticsAddressType" class="logistics-field-label"><i class="fal fa-location-dot"></i> Match against</label>
                            <select id="logisticsAddressType" [(ngModel)]="selectedAddressTypeOption" name="selectedAddressTypeOption">
                                <option value="">Any known address</option>
                                <option *ngFor="let addressOption of addressOptions" [ngValue]="addressOption">{{ addressOption }}</option>
                            </select>

                            <label class="logistics-field-label logistics-field-label--spaced"><i class="fal fa-map-location-dot"></i> UK regions</label>
                            <div class="logistics-option-list logistics-option-list--regions">
                                <label *ngFor="let region of ukRegions">
                                    <input type="checkbox" [attr.name]="'logisticsRegion' + region.name"
                                        [ngModelOptions]="{standalone: true}" [(ngModel)]="region.checked"
                                        (change)="ukRegionChanged()" />
                                    <span>{{ region.name }}</span>
                                </label>
                            </div>
                        </section>

                        <section class="logistics-foundation-block logistics-foundation-block--proximity">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-location-crosshairs"></i></span>
                                <div><strong>Postcode targeting</strong><small>Choose the level of postcode precision</small></div>
                            </header>
                            <div class="logistics-segmented-control logistics-segmented-control--postcode">
                                <button type="button" [class.active]="logisticsPostcodeMode === 'prefix'"
                                    (click)="setLogisticsPostcodeMode('prefix')">Starts with</button>
                                <button type="button" [class.active]="logisticsPostcodeMode === 'district'"
                                    (click)="setLogisticsPostcodeMode('district')">District</button>
                                <button type="button" [class.active]="logisticsPostcodeMode === 'radius'"
                                    (click)="setLogisticsPostcodeMode('radius')">Nearby</button>
                            </div>
                            @if (logisticsPostcodeMode === 'prefix') {
                            <label for="logisticsPostcodePrefix" class="logistics-field-label"><i class="fal fa-signs-post"></i> Postcode starts with</label>
                            <input id="logisticsPostcodePrefix" type="text" name="postcodeBeginsWith"
                                placeholder="e.g. EC2R 8" [(ngModel)]="postcodeBeginsWith"
                                (ngModelChange)="postcodeBeginsWithChanged()"
                                (keyup.enter)="testIfSearchCompaniesOnEnterPress($event)" />
                            }
                            @if (logisticsPostcodeMode === 'district') {
                            <label for="logisticsPostcodeDistrict" class="logistics-field-label"><i class="fal fa-map-pin"></i> Postcode district</label>
                            <input id="logisticsPostcodeDistrict" type="text" name="postcodeDistrict"
                                placeholder="e.g. EC2R" [(ngModel)]="postcodeDistrict"
                                (ngModelChange)="postcodeDistrictChanged()"
                                (keyup.enter)="testIfSearchCompaniesOnEnterPress($event)" />
                            }
                            @if (logisticsPostcodeMode === 'radius') {
                            <label for="logisticsPostcode" class="logistics-field-label"><i class="fal fa-location-dot"></i> Near postcode</label>
                            <input id="logisticsPostcode" type="text" name="postcodeWithinRadius"
                                placeholder="e.g. SW1A 1AA" [(ngModel)]="postcodeWithinRadius"
                                (ngModelChange)="postcodeWithinRadiusChanged()"
                                (keyup.enter)="testIfSearchCompaniesOnEnterPress($event)" />
                            <div class="logistics-metric-control logistics-metric-control--proximity">
                                <zint-ngx-slider #mileRadiusSliderElement *ngIf="mileRadiusRegisteredAddressRange"
                                    formName="Search radius" [isRange]="false" postfix=" miles"
                                    [abbreviateInputValues]="true"
                                    [min]="mileRadiusRegisteredAddressRange.at(0)"
                                    [max]="mileRadiusRegisteredAddressRange.at(-1)"
                                    (sliderChange)="updatePostcodeMileageRange($event)"></zint-ngx-slider>
                            </div>
                            }
                        </section>
                    </div>
                </div>
            </details>

            <details class="logistics-filter-card">
                <summary>
                    <span class="logistics-filter-card__icon"><i class="fal fa-ship"></i></span>
                    <span><strong>Trade activity</strong><small>Imports, exports and frequency</small></span>
                    <i class="fal fa-chevron-down logistics-filter-card__chevron"></i>
                </summary>
                <div class="logistics-filter-card__body">
                    <p class="logistics-trade-logic"><i class="fal fa-link"></i> Both conditions must match. Shipment cadence is measured across all traded products, not only the selected categories.</p>
                    <div class="logistics-control-groups logistics-control-groups--trade">
                        <section class="logistics-control-group">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-boxes-stacked"></i></span>
                                <div><strong>Trade profile</strong><small>Direction and products moved</small></div>
                            </header>
                            <label for="logisticsTradeDirection" class="logistics-field-label"><i class="fal fa-arrow-right-arrow-left"></i> Has recently</label>
                            <select id="logisticsTradeDirection" [(ngModel)]="selectedImportingOrExportingOption"
                                name="selectedImportingOrExportingOption">
                                <option value="">Imported or exported</option>
                                <option *ngFor="let direction of importingExportingBothOptions" [ngValue]="direction.identifier">
                                    {{ direction.description_past }}
                                </option>
                            </select>

                            <label for="logisticsTradeSearch" class="logistics-field-label logistics-field-label--spaced"><i class="fal fa-magnifying-glass"></i> Products traded</label>
                            <div class="logistics-input-action">
                                <input id="logisticsTradeSearch" type="search" name="importExportTextSearch"
                                    [(ngModel)]="importExportTextSearch" placeholder="Search commodity descriptions"
                                    (keyup.enter)="searchImportExportCodes()" />
                                <button type="button" (click)="searchImportExportCodes()" aria-label="Search trade products">
                                    <i class="fal fa-search"></i>
                                </button>
                            </div>
                            <button *ngIf="importExportTextSearch" type="button" class="logistics-inline-reset"
                                (click)="resetImportExportSearch()"><i class="fal fa-undo"></i> Show top-level categories</button>
                            <div class="logistics-tree-list logistics-tree-list--trade">
                                <ng-container *ngFor="let importExportCategory of importExportCategories">
                                    <div class="logistics-tree-row" *ngIf="importExportCategory.shouldShow">
                                        <label [style.padding-left.rem]="importExportCategory.level * 0.75">
                                            <input type="checkbox"
                                                [attr.name]="'logisticsImportExport' + importExportCategory.unique_identifier"
                                                [ngModelOptions]="{standalone: true}"
                                                [(ngModel)]="importExportCategory.checked"
                                                (change)="updateFilters(); updateToDefaultImportingOrExporting()" />
                                            <span>{{ importExportCategory.description }}</span>
                                        </label>
                                        <button type="button" (click)="expandOrCollapseImportExportCategories(importExportCategory)"
                                            [attr.aria-label]="'Expand ' + importExportCategory.description">
                                            <i class="fal" [ngClass]="importExportCategory.isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                        </button>
                                    </div>
                                </ng-container>
                            </div>
                            <div class="logistics-metric-control logistics-metric-control--trade-recency">
                                <zint-ngx-slider #importExportSliderElement *ngIf="importExportMonthRange"
                                    formName="Traded within" [isRange]="false" postfix=" months"
                                    [abbreviateInputValues]="true"
                                    [min]="importExportMonthRange[0]" [max]="importExportMonthRange[1]"
                                    (sliderChange)="updateImportExportTimeScale($event)"></zint-ngx-slider>
                            </div>
                        </section>

                        <section class="logistics-control-group">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-calendar-clock"></i></span>
                                <div><strong>Overall shipment cadence</strong><small>Minimum activity across all products</small></div>
                            </header>
                            <label for="logisticsTradeFrequencyDirection" class="logistics-field-label"><i class="fal fa-arrow-right-arrow-left"></i> Counted as</label>
                            <select id="logisticsTradeFrequencyDirection"
                                [(ngModel)]="importExportFrequencyImportingOrExporting"
                                name="importExportFrequencyImportingOrExporting">
                                <option value="">Imports or exports</option>
                                <option *ngFor="let direction of importingExportingBothOptions" [ngValue]="direction.identifier">
                                    {{ direction.description_past }}
                                </option>
                            </select>
                            <div class="logistics-metric-grid logistics-metric-grid--trade">
                                <div class="logistics-metric-control">
                                    <zint-ngx-slider #importExportFrequencySliderElement *ngIf="importExportFrequencyRange"
                                        formName="Minimum shipments" [isRange]="false" postfix=" times" maxPostfix="+"
                                        [abbreviateInputValues]="true"
                                        [min]="importExportFrequencyRange[0]" [max]="importExportFrequencyRange[1]"
                                        (sliderChange)="updateImportExportFrequency($event)"></zint-ngx-slider>
                                </div>
                                <div class="logistics-metric-control">
                                    <zint-ngx-slider #importExportFrequencyMonthCountSliderElement
                                        *ngIf="importExportFrequencyMonthCountRange" formName="Frequency period"
                                        [isRange]="false" postfix=" months"
                                        [abbreviateInputValues]="true"
                                        [min]="importExportFrequencyMonthCountRange[0]"
                                        [max]="importExportFrequencyMonthCountRange[1]"
                                        (sliderChange)="updateImportExportFrequencyMonthCount($event)"></zint-ngx-slider>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </details>

            <details class="logistics-filter-card">
                <summary>
                    <span class="logistics-filter-card__icon"><i class="fal fa-chart-line"></i></span>
                    <span><strong>Scale &amp; momentum</strong><small>Commercial size and growth</small></span>
                    <i class="fal fa-chevron-down logistics-filter-card__chevron"></i>
                </summary>
                <div class="logistics-filter-card__body">
                    <div class="logistics-control-groups logistics-control-groups--scale">
                        <section class="logistics-control-group">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-users"></i></span>
                                <div><strong>Team size</strong><small>Current workforce and hiring momentum</small></div>
                            </header>
                            <div class="logistics-metric-grid">
                                <div class="logistics-metric-control">
                                    <zint-ngx-slider #companyAgeSliderElement *ngIf="companyAgeRange"
                                        formName="Company age" postfix=" years" maxPostfix="+"
                                        [min]="companyAgeRange.at(0)" [max]="companyAgeRange.at(-1)"
                                        [abbreviateInputValues]="true"
                                        (sliderChange)="updateCompanyAgeRange($event)"></zint-ngx-slider>
                                </div>
                                <div class="logistics-metric-control">
                                    <zint-ngx-slider #headcountSliderElement *ngIf="employeeRangeFigures" formName="Headcount"
                                        postfix=" employees" maxPostfix="+" [min]="employeeRangeFigures.at(0)"
                                        [abbreviateInputValues]="true"
                                        [max]="employeeRangeFigures.at(-1)"
                                        (sliderChange)="updateEmployeeRange($event)"></zint-ngx-slider>
                                </div>
                                <div class="logistics-metric-control">
                                    <zint-ngx-slider #headcountGrowthSliderElement *ngIf="headcountGrowthPercentageRange"
                                        formName="Headcount growth" postfix="%" [min]="headcountGrowthPercentageRange.at(0)"
                                        [abbreviateInputValues]="true"
                                        [max]="headcountGrowthPercentageRange.at(-1)"
                                        (sliderChange)="updateHeadcountGrowthRange($event)"></zint-ngx-slider>
                                </div>
                            </div>
                        </section>
                        <section class="logistics-control-group">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-chart-line-up"></i></span>
                                <div><strong>Financial profile</strong><small>Scale, growth and stockholding</small></div>
                            </header>
                            <div class="logistics-metric-grid">
                                <div class="logistics-metric-control" *ngFor="let slider of logisticsFinancialSliders">
                                    <zint-ngx-slider #genericConfigSliders [sliderConfig]="slider" [abbreviateInputValues]="true"
                                        (sliderChange)="triggerConfigSliderUpdate($event, slider.label)"></zint-ngx-slider>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </details>

            <details class="logistics-filter-card">
                <summary>
                    <span class="logistics-filter-card__icon"><i class="fal fa-warehouse"></i></span>
                    <span><strong>Operations</strong><small>Technology, teams and vehicle investment</small></span>
                    <i class="fal fa-chevron-down logistics-filter-card__chevron"></i>
                </summary>
                <div class="logistics-filter-card__body">
                    <div class="logistics-control-groups">
                        <section class="logistics-control-group">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-laptop-code"></i></span>
                                <div><strong>Technology</strong><small>Logistics and e-commerce tools</small></div>
                            </header>
                            <div class="logistics-input-action logistics-input-action--quiet">
                                <input type="search" name="logisticsWebTechnologySearch"
                                    [(ngModel)]="logisticsWebTechnologySearch"
                                    (ngModelChange)="searchLogisticsWebTechnologies()"
                                    placeholder="Search logistics technologies" />
                                <button type="button" (click)="resetLogisticsWebTechnologySearch()"
                                    aria-label="Reset technology search"><i class="fal fa-undo"></i></button>
                            </div>
                            <div class="logistics-tree-list logistics-tree-list--technology">
                                <ng-container *ngFor="let technology of logisticsWebTechnologyOptions">
                                    <div class="logistics-tree-row" *ngIf="technology.shouldShow">
                                        <label [style.padding-left.rem]="technology.level * 0.75">
                                            <input type="checkbox" [attr.name]="'logisticsTechnology' + technology.name"
                                                [ngModelOptions]="{standalone: true}" [(ngModel)]="technology.checked" />
                                            <span>{{ technology.description }}</span>
                                        </label>
                                        <button *ngIf="technology.children?.length" type="button"
                                            (click)="toggleLogisticsWebTechnologyCategory(technology)"
                                            [attr.aria-label]="'Expand ' + technology.description">
                                            <i class="fal" [ngClass]="isLogisticsWebTechnologyExpanded(technology) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                        </button>
                                    </div>
                                </ng-container>
                            </div>
                        </section>
                        <section class="logistics-control-group">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-people-group"></i></span>
                                <div><strong>Operational teams</strong><small>Functions and department capacity</small></div>
                            </header>
                            <div class="logistics-option-list logistics-option-list--compact">
                                <label *ngFor="let department of logisticsFunctionalDepartmentOptions">
                                    <input type="checkbox" [attr.name]="'logisticsDepartment' + department.name"
                                        [ngModelOptions]="{standalone: true}" [(ngModel)]="department.checked" />
                                    {{ department.name }}
                                </label>
                            </div>
                            <div class="logistics-metric-control logistics-metric-control--standalone">
                                <zint-ngx-slider #departmentHeadcountSliderElement *ngIf="employeeRangeFigures"
                                    formName="Department headcount" postfix=" employees" maxPostfix="+"
                                    [abbreviateInputValues]="true"
                                    [min]="employeeRangeFigures.at(0)" [max]="employeeRangeFigures.at(-1)"
                                    (sliderChange)="updateDepartmentHeadcountRange($event)"></zint-ngx-slider>
                            </div>
                        </section>
                        <section class="logistics-control-group logistics-control-group--wide">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-truck"></i></span>
                                <div><strong>Fleet investment</strong><small>Vehicles, trailers and tangible assets</small></div>
                            </header>
                            <div class="logistics-metric-grid logistics-metric-grid--assets">
                                <div class="logistics-metric-control" *ngFor="let slider of logisticsAssetSliders">
                                    <zint-ngx-slider #genericConfigSliders [sliderConfig]="slider" [abbreviateInputValues]="true"
                                        (sliderChange)="triggerConfigSliderUpdate($event, slider.label)"></zint-ngx-slider>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </details>

            <details class="logistics-filter-card">
                <summary>
                    <span class="logistics-filter-card__icon"><i class="fal fa-bolt"></i></span>
                    <span><strong>News &amp; trigger events</strong><small>Recent events that may create logistics demand</small></span>
                    <i class="fal fa-chevron-down logistics-filter-card__chevron"></i>
                </summary>
                <div class="logistics-filter-card__body">
                    <div class="logistics-control-groups logistics-control-groups--opportunity">
                        <section class="logistics-control-group">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-newspaper"></i></span>
                                <div><strong>Event types</strong><small>Choose the company developments that matter</small></div>
                            </header>
                            <div class="logistics-news-groups">
                                <details *ngFor="let newsGroup of logisticsNewsEventGroups">
                                    <summary>
                                        <span><strong>{{ newsGroup.label }}</strong><small>{{ newsGroup.description }}</small></span>
                                        <i class="fal fa-chevron-down"></i>
                                    </summary>
                                    <div class="logistics-option-list logistics-option-list--plain">
                                        <label *ngFor="let newsOption of newsGroup.options">
                                            <input type="checkbox"
                                                [attr.name]="'logisticsNews' + newsOption.label"
                                                [checked]="isLogisticsNewsOptionSelected(newsOption.categoryNames)"
                                                (change)="setLogisticsNewsOption(newsOption.categoryNames, $any($event.target).checked)" />
                                            {{ newsOption.label }}
                                        </label>
                                    </div>
                                </details>
                            </div>
                        </section>
                        <section class="logistics-control-group">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-clock"></i></span>
                                <div><strong>Recency</strong><small>How recent the signal should be</small></div>
                            </header>
                            <div class="logistics-metric-control logistics-metric-control--standalone">
                                <zint-ngx-slider #newsDayRangeSliderElement *ngIf="newsDaysRange" formName="News published within"
                                    [isRange]="false" postfix=" days" [min]="newsDaysRange.at(0)" [max]="newsDaysRange.at(-1)"
                                    [abbreviateInputValues]="true"
                                    (sliderChange)="updateNewsDays($event)"></zint-ngx-slider>
                            </div>
                        </section>
                    </div>
                </div>
            </details>

            <details class="logistics-filter-card">
                <summary>
                    <span class="logistics-filter-card__icon"><i class="fal fa-layer-group"></i></span>
                    <span><strong>Your data &amp; engagement</strong><small>Workspace fields and team activity</small></span>
                    <i class="fal fa-chevron-down logistics-filter-card__chevron"></i>
                </summary>
                <div class="logistics-filter-card__body">
                    <div class="logistics-control-groups logistics-control-groups--workspace-data">
                        <section class="logistics-control-group">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-table-columns"></i></span>
                                <div><strong>Your workspace fields</strong><small>Filter using data your organisation has added to Zint</small></div>
                            </header>
                            <div class="logistics-input-action logistics-input-action--quiet">
                                <input type="search" name="customColumnSearchTerm" minlength="2" maxlength="32"
                                    placeholder="Find one of your fields" [(ngModel)]="customColumnSearchTerm"
                                    (ngModelChange)="filterCustomColumns()" />
                                <button type="button" (click)="customColumnSearchTerm = ''; filterCustomColumns()"
                                    aria-label="Reset workspace field search"><i class="fal fa-undo"></i></button>
                            </div>
                            @if (filteredCustomColumns?.length) {
                            <div class="logistics-custom-fields">
                                <custom-column-filter
                                    [inputParams]="{'customColumns': filteredCustomColumns, 'filterOnAttribute': 'is_basic_column', 'equalsOrNot': true, 'teamMembers': teamMembersForCustomColumns, 'logisticsMode': true}"></custom-column-filter>
                            </div>
                            } @else {
                            <p class="logistics-empty-note">No matching workspace fields.</p>
                            }
                        </section>
                        <section class="logistics-control-group">
                            <header class="logistics-control-group__header">
                                <span><i class="fal fa-pepper-hot"></i></span>
                                <div><strong>Team engagement</strong><small>Find companies by their recent Zint Heat Score</small></div>
                            </header>
                            <div class="logistics-metric-control">
                                <ng-container *ngFor="let slider of zintSlidersConfigs['heatScore']">
                                    <zint-ngx-slider #heatScoreSliders [sliderConfig]="slider"
                                        (sliderChange)="triggerConfigSliderUpdate($event, slider.label)"></zint-ngx-slider>
                                </ng-container>
                            </div>
                            <p class="logistics-help-text"><i class="fal fa-circle-info"></i> Heat Score reflects activity from your team over the last 30 days.</p>
                        </section>
                    </div>
                </div>
            </details>
        </div>

        <section class="logistics-intelligence">
            <div class="logistics-section-heading logistics-section-heading--intelligence">
                <div>
                    <span class="logistics-section-number">02</span>
                    <div><span class="logistics-eyebrow">MAGIC INSIGHTS</span><h2>Qualify the operational opportunity</h2></div>
                </div>
                <p class="logistics-ai-disclaimer"><i class="fal fa-wand-sparkles"></i> AI-generated estimates based on available company information. Treat them as directional and verify important details.</p>
            </div>

            <nav class="logistics-service-nav" aria-label="Logistics service areas">
                @for (section of logisticsMagicInsightSections; track section.id) {
                <button type="button" [class.active]="section.id === activeLogisticsServiceSection"
                    (click)="setActiveLogisticsServiceSection(section.id)">
                    <i class="fal {{ section.icon }}"></i>
                    <span><strong>{{ section.title }}</strong><small>{{ section.filterCount || section.expectedFilterCount }} {{ (section.filterCount || section.expectedFilterCount) === 1 ? 'filter' : 'filters' }}</small></span>
                </button>
                }
            </nav>

            @if (activeLogisticsMagicInsightSection) {
            <div class="logistics-intelligence-panel">
                <header>
                    <span class="logistics-intelligence-panel__icon"><i class="fal {{ activeLogisticsMagicInsightSection.icon }}"></i></span>
                    <div>
                        <span class="logistics-eyebrow">{{ activeLogisticsMagicInsightSection.eyebrow }}</span>
                        <h3>{{ activeLogisticsMagicInsightSection.title }}</h3>
                        <p>{{ activeLogisticsMagicInsightSection.description }}</p>
                    </div>
                    <span class="logistics-filter-count">{{ activeLogisticsMagicInsightSection.filterCount }}/{{ activeLogisticsMagicInsightSection.expectedFilterCount }} available</span>
                </header>

                @if (isLoading().loadingMagicFilters) {
                <div class="logistics-intelligence-state">
                    <i class="fa-solid fa-wand-sparkles fa-flip"></i>
                    <strong>Loading logistics intelligence…</strong>
                </div>
                } @else if (!activeLogisticsMagicInsightSection.groups.length) {
                <div class="logistics-intelligence-state logistics-intelligence-state--empty">
                    <span><i class="fal fa-lock"></i></span>
                    <div><strong>This intelligence set is not available on the current account.</strong>
                    <p>The workspace preserves all {{ activeLogisticsMagicInsightSection.expectedFilterCount }} controls here when the corresponding production module is entitled.</p></div>
                </div>
                } @else {
                <div class="logistics-intelligence-grid">
                    @for (group of activeLogisticsMagicInsightSection.groups; track group.moduleName + group.questionId + group.groupKey) {
                    <article class="logistics-intelligence-card" [class.is-locked]="!group.hasFilterAccess">
                        <header>
                            <div><h4>{{ group.questionTitle }}</h4></div>
                            @if (!group.hasFilterAccess) {
                            <i class="fal fa-lock" matTooltip="Upgrade the current plan to use these filters"></i>
                            }
                        </header>
                        @if (group.filters.length > 1) {
                        <p class="logistics-linked-note"><i class="fal fa-link"></i> These fields work together</p>
                        }
                        <div class="logistics-intelligence-card__fields">
                            @for (filter of group.filters; track filter.label) {
                            @if (filter.filterType === 'range_slider') {
                            <div class="logistics-magic-field">
                                <zint-ngx-slider #genericConfigSliders
                                    [sliderConfig]="getLogisticsSliderConfig(filter)"
                                    [abbreviateInputValues]="true"
                                    (sliderChange)="triggerConfigSliderWithObjectUpdate($event, filter)"></zint-ngx-slider>
                                <details class="logistics-confidence">
                                    <summary><i class="fal fa-gauge-high"></i> Confidence</summary>
                                    <zint-ngx-slider #confidenceSliders
                                        [formName]="'confidence:' + filter.label + 'Aira' + filter.airaQuestionId"
                                        [min]="0" [max]="100" postfix="%"
                                        (sliderChange)="triggerConfidenceSliderUpdate($event, filter)"></zint-ngx-slider>
                                </details>
                            </div>
                            }
                            @if (filter.filterType === 'text_input') {
                            <div class="logistics-magic-field">
                                <label>{{ filter.label }}</label>
                                <input type="text" [name]="'logisticsMagic' + filter.airaQuestionId + filter.fieldName"
                                    [(ngModel)]="filter.value"
                                    (change)="triggerConfigTextInputWithObjectUpdate(filter.value, filter)"
                                    placeholder="Type a word or phrase" />
                                <details class="logistics-confidence">
                                    <summary><i class="fal fa-gauge-high"></i> Confidence</summary>
                                    <zint-ngx-slider #confidenceSliders
                                        [formName]="'confidence:' + filter.label + 'Aira' + filter.airaQuestionId"
                                        [min]="0" [max]="100" postfix="%"
                                        (sliderChange)="triggerConfidenceSliderUpdate($event, filter)"></zint-ngx-slider>
                                </details>
                            </div>
                            }
                            @if (filter.filterType === 'multi_select') {
                            <div class="logistics-magic-field">
                                <label>{{ filter.label }}</label>
                                @if (filter.options_type === 'countries_and_territories') {
                                <div class="logistics-input-with-icon">
                                    <i class="fal fa-search"></i>
                                    <input type="search" placeholder="Find a country"
                                        [name]="'logisticsMagicCountry' + filter.airaQuestionId + filter.fieldName"
                                        (input)="filterMagicCountriesList($event.target.value, group.moduleName, filter.linkedFieldName || filter.fieldName)" />
                                </div>
                                }
                                <div class="logistics-magic-options">
                                    @for (optionElement of filter.options; track optionElement.value) {
                                    <label *ngIf="filter.options_type ? optionElement.shouldShow : true">
                                        <input type="checkbox" [name]="'logisticsMagicOption' + filter.airaQuestionId + filter.fieldName + optionElement.value"
                                            [(ngModel)]="optionElement.checked"
                                            (ngModelChange)="triggerConfigMultiSelectInputWithObjectUpdate(filter)" />
                                        <span>{{ optionElement.name || optionElement.label }}</span>
                                    </label>
                                    }
                                </div>
                                <details class="logistics-confidence">
                                    <summary><i class="fal fa-gauge-high"></i> Confidence</summary>
                                    <zint-ngx-slider #confidenceSliders
                                        [formName]="'confidence:' + filter.label + 'Aira' + filter.airaQuestionId"
                                        [min]="0" [max]="100" postfix="%"
                                        (sliderChange)="triggerConfidenceSliderUpdate($event, filter)"></zint-ngx-slider>
                                </details>
                            </div>
                            }
                            }
                        </div>
                    </article>
                    }
                </div>
                }
            </div>
            }
        </section>
    </section>

        <mat-drawer-container autosize>

            <mat-drawer-content>
            <div class="main-grid">

                <!-- reset and search params tags box -->
                @if (searchTags && searchTags.length > 0) {
                    <div class="card mb-2 logistics-search-summary" style="border-radius: 8px">
                        <div class="card-body search-actions-container">
                            <div class="logistics-search-summary__actions">
                                <button type="button" class="logistics-reset-button" (click)="scrollToLogisticsFilters()">
                                    <i class="fal fa-sliders-simple mr-1"></i>Refine search
                                </button>
                                <button type="button" class="logistics-reset-button logistics-reset-button--quiet" (click)="resetLogisticsFilters()">
                                    <i class="fal fa-undo mr-1"></i>Clear all
                                </button>
                            </div>
                            <search-parameters [inputParams]="{'searchTags': searchTags, 'enableClearing': true, 'logisticsMode': true}"
                                [savedSearchName]="savedSearchParamName"
                                (onClearSearchParam)="clearSearchParam($event[0])"></search-parameters>
                        </div>
                    </div>
                }


                <section id="logistics-results-section" class="logistics-results" aria-labelledby="logisticsResultsHeading">
                    <header class="logistics-results__header">
                        <div>
                            <span class="logistics-eyebrow">MATCHING COMPANIES</span>
                            <h2 id="logisticsResultsHeading">
                                @if (isLoading().loadingCompanyCount) {
                                Finding your prospects…
                                } @else {
                                <span *ngIf="!companyCount().exact">About </span>{{ companyCount().count | number }}
                                <span *ngIf="companyCount().active_only"> active</span> companies match
                                }
                            </h2>
                        </div>
                        <div class="logistics-results__header-actions">
                            <button type="button" class="logistics-secondary-button" (click)="openResultsActionModal('saveSearch')">
                                <i class="fal fa-bookmark"></i><span>Save search</span>
                            </button>
                            <button type="button" class="logistics-secondary-button" (click)="scrollToLogisticsFilters()">
                                <i class="fal fa-sliders-simple"></i><span>Refine search</span>
                            </button>
                        </div>
                    </header>

                    <div class="logistics-results-perspective" aria-label="Choose which logistics signals the company cards prioritise">
                        <div class="logistics-results-perspective__intro">
                            <span>Card view</span>
                            <small>Reorders the insights shown. Your search stays the same.</small>
                        </div>
                        <div class="logistics-results-perspective__options" role="group" aria-label="Logistics operative mode">
                            @for (perspective of logisticsResultsPerspectives; track perspective.id) {
                            <button type="button"
                                [class.is-active]="logisticsResultsPerspective === perspective.id"
                                [attr.aria-pressed]="logisticsResultsPerspective === perspective.id"
                                [matTooltip]="perspective.description"
                                matTooltipClass="logistics-action-tooltip"
                                (click)="setLogisticsResultsPerspective(perspective.id)">
                                <i class="fal {{ perspective.icon }}"></i>
                                <span class="logistics-results-perspective__full-label">{{ perspective.label }}</span>
                                <span class="logistics-results-perspective__short-label">{{ perspective.shortLabel }}</span>
                            </button>
                            }
                        </div>
                    </div>

                    <div class="logistics-results-toolbar">
                        <div class="logistics-results-toolbar__selection">
                            <label class="logistics-select-page">
                                <input type="checkbox" name="logisticsSelectAllCompanies"
                                    [(ngModel)]="checkboxSelectAllCompanies"
                                    (ngModelChange)="showOrHideInfoOnAllSelectedCompanies()" />
                                <span>Select this page</span>
                            </label>
                            @if (totalCompaniesSelected) {
                            <span class="logistics-selection-count">{{ totalCompaniesSelected | number }} selected</span>
                            @if (!hasSelectedTotalNumberMatchingFilter && companyCount().count !== totalCompaniesSelected) {
                            <button type="button" class="logistics-text-button" (click)="hasSelectedAllCompaniesByQuery()">
                                Select all <span *ngIf="!companyCount().exact">approximately </span>{{ companyCount().count | number }} matches
                            </button>
                            }
                            }
                        </div>

                        @if (totalCompaniesSelected) {
                        <div class="logistics-bulk-actions" aria-label="Actions for selected companies">
                            <button type="button" data-toggle="modal" data-target="#tagModal" matTooltip="Tag or untag selected companies" matTooltipClass="logistics-action-tooltip">
                                <i class="fal fa-tag"></i><span>Tag</span>
                            </button>
                            <button type="button" (click)="toggleDownloadModal()" matTooltip="Download selected companies" matTooltipClass="logistics-action-tooltip">
                                <i class="fal fa-download"></i><span>Download</span>
                            </button>
                            @for (crm of availableCRMList; track $index) {
                                @if (crm.functionalityEnabled) {
                                <button type="button" (click)="openCRMExportModal(crm.name)" matTooltip="Export selected companies to {{ crm.name }}" matTooltipClass="logistics-action-tooltip">
                                    <span [innerHTML]="getCRMIcon(crm.crmType)"></span><span>{{ crm.name }}</span>
                                </button>
                                }
                            }
                            <button type="button" data-toggle="modal" data-target="#customColumnEditModal" matTooltip="Bulk edit attributes" matTooltipClass="logistics-action-tooltip">
                                <i class="fal fa-pen-field"></i><span>Attributes</span>
                            </button>
                            @if (hasAIRAAccess) {
                            <button type="button" (click)="openGetInsights()" matTooltip="Get Magic Insights for selected companies" matTooltipClass="logistics-action-tooltip">
                                <i class="fal fa-wand-sparkles"></i><span>Get insights</span>
                            </button>
                            }
                        </div>
                        }

                        <div class="logistics-results-toolbar__controls">
                            <label>
                                <span>Sort</span>
                                <select name="logisticsResultSort" [ngModel]="sortKey || ''"
                                    (ngModelChange)="updateLogisticsSortKey($event)">
                                    <option value="">Default order</option>
                                    @if (summarySemanticSearchTerm) {
                                    <option [value]="semanticSearchScoreSortKey">Best product match</option>
                                    }
                                    @for (field of getLogisticsSortableFields(); track field) {
                                        @if (field !== semanticSearchScoreSortKey) {
                                        <option [value]="field">{{ field }}</option>
                                        }
                                    }
                                </select>
                            </label>
                            <button type="button" class="logistics-sort-direction" (click)="toggleLogisticsSortDirection()"
                                [disabled]="!sortKey" [attr.aria-label]="sortDirectionIsAscending ? 'Sort descending' : 'Sort ascending'">
                                <i class="fal" [ngClass]="sortDirectionIsAscending ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
                            </button>
                            <label>
                                <span>Per page</span>
                                <select name="logisticsPageSize" [ngModel]="selectedPageSizeLimit"
                                    (ngModelChange)="updateLogisticsPageSize($event)">
                                    @for (limit of [10,25,50,100]; track limit) {
                                    <option [ngValue]="limit" [disabled]="limit > maxPageSizeLimit">{{ limit }}</option>
                                    }
                                </select>
                            </label>
                        </div>
                    </div>

                    @if (isLoading().loadingCompanies) {
                    <div class="logistics-results-list" aria-label="Loading companies">
                        @for (placeholder of [1, 2, 3]; track placeholder) {
                        <article class="logistics-company-card logistics-company-card--loading">
                            <span></span><span></span><span></span>
                        </article>
                        }
                    </div>
                    } @else if (companies?.length) {
                    <div class="logistics-results-list">
                        @for (company of companies; track company.id) {
                        <article class="logistics-company-card" [class.is-selected]="company.checked">
                            <header class="logistics-company-card__header">
                                <label class="logistics-company-card__select" matTooltip="Select this company">
                                    <input type="checkbox" name="logisticsCompanySelectedOption{{ company.id }}"
                                        (change)="updateCountOfSelectedCompanies()" [(ngModel)]="company.checked" />
                                    <span class="sr-only">Select {{ company['Company Name'] }}</span>
                                </label>
                                <span class="logistics-company-card__monogram" aria-hidden="true">{{ company['Company Name']?.charAt(0) || 'C' }}</span>
                                <div class="logistics-company-card__identity">
                                    <div class="logistics-company-card__name">
                                        <a [href]="'/profile/' + company.number" target="_blank">{{ company['Company Name'] }}</a>
                                    </div>
                                    <span>
                                        Company no. {{ company.number }}
                                        @if (getLogisticsWebsiteHref(company)) {
                                        <span class="logistics-company-card__identity-separator">·</span>
                                        <a class="logistics-company-card__website" [href]="getLogisticsWebsiteHref(company)"
                                            target="_blank" rel="noopener">
                                            <i class="fal fa-globe"></i>{{ getLogisticsWebsiteLabel(company) }}
                                        </a>
                                        }
                                    </span>
                                </div>
                                <div class="logistics-company-card__badges">
                                    @if (company.hasMIAvailable === MIAvailabilityStatus.hasMI) {
                                    <span class="logistics-result-badge logistics-result-badge--insight"><i class="far fa-sparkle"></i> Insights available</span>
                                    }
                                    @if (company.hasViewed) {
                                    <span class="logistics-result-badge"><i class="fal fa-eye"></i> Viewed</span>
                                    }
                                </div>
                                <div class="logistics-company-card__actions">
                                    @if (!isScreenTooSmall()) {
                                    <button type="button" (click)="toggleProfilePreview(company.number)">
                                        <i class="fal fa-window-maximize"></i><span>Preview</span>
                                    </button>
                                    }
                                    <a [href]="'/profile/' + company.number" target="_blank">
                                        <span>View company</span><i class="fal fa-arrow-up-right-from-square"></i>
                                    </a>
                                </div>
                            </header>

                            @if (getLogisticsAvailableInsightCount(company)) {
                            <div class="logistics-company-card__match">
                                <span><i class="fal fa-wand-sparkles"></i> Magic Insights</span>
                                <strong>
                                    @if (getLogisticsAvailableInsightCount(company) === getLogisticsVisibleInsightCount(company)) {
                                    {{ getLogisticsAvailableInsightCount(company) }} available for this company
                                    } @else {
                                    {{ getLogisticsVisibleInsightCount(company) }} displayed · {{ getLogisticsAvailableInsightCount(company) }} available for this company
                                    }
                                </strong>
                            </div>
                            }

                            <dl class="logistics-company-card__fields">
                                @for (field of getLogisticsCoreFields(company); track field.key) {
                                <div>
                                    <dt><i class="fal {{ field.icon }}"></i> {{ field.label }}</dt>
                                    <dd>
                                        @if (hasLogisticsResultValue(field.val)) {
                                        <span [innerHtml]="field.val | companyDataPipe:field.key:company.number:accountsSearchTerm:homepageSearchTerm:fullWebsiteSearchTerm:newsContaining"></span>
                                        } @else {
                                        <span class="logistics-result-empty" title="Not reported">—</span>
                                        }
                                    </dd>
                                </div>
                                }
                            </dl>

                            @if (getLogisticsProductsAndServices(company).length) {
                            <div class="logistics-company-card__products">
                                <span><i class="fal fa-box-open"></i> Products &amp; services</span>
                                <div>
                                    @for (productOrService of getLogisticsProductsAndServices(company); track productOrService) {
                                    <span>{{ productOrService }}</span>
                                    }
                                </div>
                            </div>
                            }

                            @if (getLogisticsAvailableInsightCount(company)) {
                            <div class="logistics-company-card__insights">
                                @for (panel of getLogisticsCardPanels(company); track panel.priorityKey) {
                                @if (panel.panelType === 'provider') {
                                <div class="logistics-company-card__insight logistics-company-card__insight--providers">
                                    <span class="logistics-mi-marker" title="Magic Insight" aria-label="Magic Insight"><i class="fal fa-wand-sparkles"></i></span>
                                    <div class="logistics-provider-summary__heading">
                                        <span><i class="fal fa-truck-fast"></i></span>
                                        <small>{{ getLogisticsStructuredInsightLabel(panel.insight) }}</small>
                                    </div>
                                    <div class="logistics-provider-list">
                                        @for (provider of getLogisticsProviders(company); track provider.domain || provider.name || $index) {
                                        <span class="logistics-provider-chip" [title]="provider.services_provided?.join(', ') || provider.name">
                                            @if (provider.domain) {
                                            <img [src]="'https://www.google.com/s2/favicons?sz=64&domain=' + provider.domain"
                                                [alt]="provider.name + ' logo'" />
                                            } @else {
                                            <i class="fal" [ngClass]="getLogisticsProviderInsight(company).displaySchema?.fallback_icon || 'fa-truck'"></i>
                                            }
                                            <span>{{ provider.name }}</span>
                                        </span>
                                        }
                                        @if (getLogisticsProviderOverflowCount(company)) {
                                        <span class="logistics-provider-more">+{{ getLogisticsProviderOverflowCount(company) }}</span>
                                        }
                                    </div>
                                </div>
                                }
                                @if (panel.panelType === 'directional') {
                                <div class="logistics-company-card__insight logistics-company-card__insight--directional"
                                    [class.logistics-company-card__insight--geography]="panel.group.displayMode === 'geography'">
                                    <span class="logistics-mi-marker" title="Magic Insight" aria-label="Magic Insight"><i class="fal fa-wand-sparkles"></i></span>
                                    <div class="logistics-directional-insight__heading">
                                        <span class="logistics-company-card__insight-icon"><i class="fal {{ panel.group.icon }}"></i></span>
                                        <div>
                                            <strong class="logistics-insight-kind">{{ panel.group.label }}</strong>
                                            <small>{{ panel.group.period }}{{ panel.group.displayMode === 'metrics' ? ' movement' : '' }}</small>
                                        </div>
                                    </div>
                                    @if (panel.group.displayMode === 'geography') {
                                    <div class="logistics-geographical-insight__values">
                                        @for (field of panel.group.fields; track field.fieldKey) {
                                        <div>
                                            <span class="logistics-geographical-insight__direction">{{ field.label }}</span>
                                            <div class="logistics-geographical-insight__countries">
                                                @for (country of getLogisticsGeographicalBreakdown(field.insight); track country.countryCode) {
                                                <span [title]="country.countryName">
                                                    <strong>{{ country.countryCode }}</strong>
                                                    <small>{{ country.percentage | number:'1.0-1' }}%</small>
                                                </span>
                                                }
                                                @if (getLogisticsGeographicalOverflowCount(field.insight)) {
                                                <span class="logistics-geographical-insight__more">+{{ getLogisticsGeographicalOverflowCount(field.insight) }}</span>
                                                }
                                            </div>
                                        </div>
                                        }
                                    </div>
                                    } @else {
                                    <div class="logistics-directional-insight__values">
                                        @for (field of panel.group.fields; track field.fieldKey) {
                                        <div>
                                            <span>{{ field.label }}</span>
                                            <strong>{{ formatLogisticsInsightNumber(field.insight, false) }}</strong>
                                        </div>
                                        }
                                    </div>
                                    }
                                </div>
                                }
                                @if (panel.panelType === 'insight') {
                                <div class="logistics-company-card__insight">
                                    <span class="logistics-mi-marker" title="Magic Insight" aria-label="Magic Insight"><i class="fal fa-wand-sparkles"></i></span>
                                    <span class="logistics-company-card__insight-icon"><i class="fal {{ getLogisticsStructuredInsightIcon(panel.insight) }}"></i></span>
                                    <div>
                                        <small>{{ getLogisticsStructuredInsightLabel(panel.insight) }}</small>
                                        @if (panel.insight.displaySchema?.display_type === 'number' || panel.insight.displaySchema?.display_type === 'gauge') {
                                        <strong>{{ formatLogisticsInsightNumber(panel.insight) }}</strong>
                                        } @else if (getLogisticsInsightPills(panel.insight).length) {
                                        <div class="logistics-insight-pills">
                                            @for (pill of getLogisticsInsightPills(panel.insight).slice(0, 3); track pill) {
                                            <span>{{ pill }}</span>
                                            }
                                            @if (getLogisticsInsightPills(panel.insight).length > 3) {
                                            <span>+{{ getLogisticsInsightPills(panel.insight).length - 3 }}</span>
                                            }
                                        </div>
                                        } @else {
                                        <strong [innerHtml]="panel.insight.value | companyDataPipe:panel.insight.legacyKey:company.number:accountsSearchTerm:homepageSearchTerm:fullWebsiteSearchTerm:newsContaining"></strong>
                                        }
                                    </div>
                                </div>
                                }
                                }
                            </div>
                            }
                        </article>
                        }

                        @if (companyCount().locked_mi_companies && getPlaceholderRowCount() > 0) {
                        <aside class="logistics-locked-results">
                            <span><i class="fal fa-wand-sparkles"></i></span>
                            <div>
                                <strong>{{ companyCount().locked_mi_companies | number }} more companies match this search</strong>
                                <p>Unlock the existing logistics analysis to see who they are.</p>
                            </div>
                            <button type="button" (click)="unlockPlaceholderMagicInsights()">Get insights</button>
                        </aside>
                        }
                    </div>
                    } @else {
                    <div class="logistics-results-empty">
                        <span><i class="fal fa-radar"></i></span>
                        <h3>No matching companies yet</h3>
                        <p>Broaden one or two criteria, then run the search again.</p>
                        <button type="button" class="logistics-secondary-button" (click)="scrollToLogisticsFilters()">Refine search</button>
                    </div>
                    }

                    @if (!isLoading().loadingCompanies && maxPageNumber > 1) {
                    <nav class="logistics-results-pagination" aria-label="Company results pages">
                        <button type="button" (click)="previousPage()" [disabled]="pageNumber === 1">
                            <i class="fal fa-arrow-left"></i><span>Previous</span>
                        </button>
                        <label>
                            <span>Page</span>
                            <input #logisticsPageInput type="number" min="1" [max]="maxPageNumber" [value]="pageNumber"
                                (keyup.enter)="definedPage(logisticsPageInput.value)" />
                            <span>of {{ maxPageNumber | number }}</span>
                        </label>
                        <button type="button" (click)="nextPage()" [disabled]="pageNumber === maxPageNumber">
                            <span>Next</span><i class="fal fa-arrow-right"></i>
                        </button>
                    </nav>
                    }
                </section>

            </div>
            </mat-drawer-content>
            @if(!isScreenTooSmall()){
            <mat-drawer #profilePreview [style.width]="previewDrawerWidth()" position="end" mode="side"
            [opened]="profilePreviewIsOpen()" (openedChange)="drawerStateChanged($event)"
            >
                <button class="rounded-btn-base rounded-btn-white rounded-left-btn preview-drawer-close btn-p-sm no-focus-outline" (click)="toggleProfilePreview()" matTooltip="Close" [style.right]="previewDrawerWidth() === '820px' ? '825px': '590px'">
                    <i class="fal fa-right-to-bracket"></i>
                </button>
            @if (dataStore.companyNumberFromParent()) {
            <company-profile-page [companyNumberFromParent]="dataStore.companyNumberFromParent()" (endpointsLoaded)="triggerChangeDetection($event)"></company-profile-page>
            } @else if (!dataStore.companyNumberFromParent()) {
                <span>No Preview Available</span>
            }
            </mat-drawer>
        }
        </mat-drawer-container>

</div>

<div class="modal fade" id="searchResultsActionsModal" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
        <div class="modal-content">
            <div class="modal-header py-2 d-flex align-items-baseline">
                <h5 class="modal-title">Save search</h5>
                <button type="button" class="close" data-dismiss="modal"><span>&times;</span></button>
            </div>
            <div class="modal-body">
                <div class="input-group">
                    <div class="input-group-prepend" *ngIf="isSuperUser">
                        <label class="input-group-text" style="font-size: 10px; border-radius: 8px 0 0 8px;height: 33px;">
                            <input type="checkbox" name="makeSavedSearchShared" value="false" [(ngModel)]="makeSavedSearchShared" />
                            <span>&nbsp;Shared across org?</span>
                        </label>
                    </div>
                    <input type="text" class="form-control" style="height: 33px;" [style.border-radius]="!isSuperUser ? '8px 0 0 8px !important' : null" name="savedSearchName" [(ngModel)]="savedSearchName" placeholder="Choose a name">
                    <div class="input-group-append">
                        <button class="rounded-btn-base rounded-btn-light btn-p-sm rounded-right-btn" type="button" (click)="saveSearch()">
                            <i class="fal fa-save"></i> Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<get-insights #getInsights [modulesInfo]="magicModuleNamesIds"
  modalId="dashboardGetInsightsModal"
  [numberOfCompaniesMatchingSearchParams]="!isLoading().loadingCompanyCount ? companyCount().count : null"
  (completed)="handleGetInsightsCompleted($event)"></get-insights>


@if(pushMessagesService.showPopupPushMsg()) {
    <div class="modal fade" id="gr-popup-modal" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div class="modal-content">
                <div class="align-self-end">
                    <button type="button" class="position-absolute rounded-btn-base rounded-btn-light btn-p-xs mt-1" style="z-index: 2; right: 0.25rem;" data-dismiss="modal" aria-label="Close" (click)="pushMessagesService.closePopupWindow()" matTooltip="Hide for this session">
                        &times;
                    </button>
                </div>

                <div  class="embed-responsive embed-responsive-16by9">
                    <iframe class="" [src]="pushMessagesService.popupMsg.embed_url | safe" frameborder="0" allowfullscreen></iframe>
                </div>
                <div class="modal-footer p-0">
                    <small role="button" class="rounded-btn-base rounded-btn-dark-hover mr-2" (click)="pushMessagesService.triggerIgnoreMsg(pushMessagesService.popupMsg)" data-dismiss="modal" matTooltip="Don't show this message again">Ignore
                    </small>
                </div>
            </div>
        </div>
    </div>
}
